// The ONE way an authenticated request leaves this app.
//
// Every call acquires the bearer FROM MSAL'S CACHE AT SEND TIME (a localStorage
// read — no network unless a refresh is actually due) and replays ONCE on an auth
// rejection. Nothing here reads React state, so the token a request carries can
// never be a stale copy. The React-state snapshot in useAuthentication is for
// `authReady` gating (don't fire before the shell has a token), not for requests.
//
// Why this exists — observed live 2026-09-10 (PR #219): the app sends the ID
// token as the API bearer, but MSAL decides whether to refresh by looking at the
// ACCESS token. Entra jitters access-token life 60–90 min vs ~60 for the ID token,
// so for up to ~25 min in some hours MSAL's cache holds a valid access token next
// to an EXPIRED ID token and reports success. The seven screens that acquired per
// request and retried once (useAuthFetch) kept working through that window; the
// ~57 files that sent a copy from React state broke until the operator logged out.
// Now every caller behaves like the seven that worked.
//
// Two guards, in order:
//   1. Before sending: if the cached bundle's ID token is within LEAD_MS of its
//      OWN `exp`, force a refresh first. MSAL will not do this on its own — it
//      only watches the access token.
//   2. After sending: on an AUTH rejection (isAuthRejection) force a refresh and
//      replay exactly once. Covers clock skew and a token that died in transit.
//      Never loops.
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import type { AccountInfo, AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { beginInteractiveReauth } from "../hooks/useAuthentication";

// Same scopes as the provider so every acquire hits the SAME MSAL cache entry
// (different scopes = a different entry = a separate refresh to keep alive).
export const AUTH_SCOPES = ["User.Read"];

// Don't send a token with less than this left: it can expire in transit, and the
// backend's leeway is only 30 s (config/auth_tools.py). The provider renews at
// −2 min in the normal case; this is the per-request backstop (laptop asleep
// through the timer, a tab restored from bfcache, …).
const LEAD_MS = 30_000;

// The two 403 bodies config/auth.py `login_required` emits that mean "your BEARER
// is bad" rather than "YOU are not allowed" (it answers 403, not 401, for an
// expired/invalid token). Every other 403 — "User account is not active.", a
// four-eyes refusal, Checkpoint access, … — is a real answer and must not be
// replayed: a POST refused for a business reason must not be re-POSTed.
const TOKEN_REJECTION_BODIES = new Set(["Token has expired.", "Invalid token."]);

let msal: IPublicClientApplication | null = null;

// main.tsx calls this ONCE, right after `msalInstance.initialize()`; tests register
// a fake. Until it's called — or with no signed-in account — requests go out with
// no bearer, which is exactly the local-dev path (VITE_BYPASS_AUTH + the backend's
// IS_LOCAL) and what `authHeaders(null)` used to do.
export const registerMsalInstance = (instance: IPublicClientApplication | null): void => {
  msal = instance;
};

const activeAccount = (): AccountInfo | null => {
  if (!msal) return null;
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
};

const idTokenExpiresAtMs = (result: AuthenticationResult): number | null => {
  const exp = (result.idTokenClaims as { exp?: number } | undefined)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
};

// A burst of requests inside a stale window must cost ONE forced refresh, not one
// per request: MSAL does not dedupe forceRefresh calls, and each is a round-trip
// to Entra's token endpoint.
let forcedRefreshInFlight: Promise<string | null> | null = null;

const onAcquireError = (instance: IPublicClientApplication, account: AccountInfo, error: unknown): null => {
  if (error instanceof InteractionRequiredAuthError) {
    // Entra REFUSED silent renewal — the one case that genuinely needs the user.
    // Route through the shared single-flight latch so a burst of failing fetches
    // can't each launch their own redirect.
    beginInteractiveReauth(instance, account);
  }
  // Transient (network blip, timeout) or interaction pending: send with no bearer;
  // the backend 401s cleanly and the caller's normal error path runs.
  return null;
};

/**
 * The ID token to send RIGHT NOW, or null when there is no signed-in account or
 * MSAL can't provide one. `force` bypasses MSAL's cache and mints a fresh bundle
 * (single-flighted across concurrent callers).
 */
export const getIdToken = async (force = false): Promise<string | null> => {
  const instance = msal;
  const account = activeAccount();
  if (!instance || !account) return null;

  if (force) {
    if (!forcedRefreshInFlight) {
      forcedRefreshInFlight = instance
        .acquireTokenSilent({ account, scopes: AUTH_SCOPES, forceRefresh: true })
        .then((result) => result.idToken)
        .catch((error) => onAcquireError(instance, account, error))
        .finally(() => {
          forcedRefreshInFlight = null;
        });
    }
    return forcedRefreshInFlight;
  }

  let result: AuthenticationResult;
  try {
    result = await instance.acquireTokenSilent({ account, scopes: AUTH_SCOPES });
  } catch (error) {
    return onAcquireError(instance, account, error);
  }
  // Guard 1: this is the CACHED bundle. Its ID token can already be dead while
  // the access token beside it looks healthy — judge it by its OWN exp.
  const expiresAt = idTokenExpiresAtMs(result);
  if (expiresAt !== null && expiresAt - Date.now() < LEAD_MS) return getIdToken(true);
  return result.idToken;
};

/**
 * True when the response says the BEARER was rejected, as opposed to the caller
 * being forbidden. 401 is always auth here. 403 only when the body is one of
 * login_required's token errors — read from a clone so the caller's body is
 * untouched. Anything unparseable is treated as a real 403.
 */
export const isAuthRejection = async (response: Response): Promise<boolean> => {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;
  try {
    const body = (await response.clone().json()) as { error?: unknown } | null;
    return typeof body?.error === "string" && TOKEN_REJECTION_BODIES.has(body.error);
  } catch {
    return false;
  }
};

/**
 * `fetch` with a fresh bearer attached at send time and ONE replay on an auth
 * rejection. Drop-in for `fetch(url, { headers: authHeaders(token), ... })`:
 * callers keep their own headers (Content-Type etc.); Authorization is added.
 */
export const authedFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const send = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { ...((init.headers ?? {}) as Record<string, string>) };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...init, headers });
  };

  const token = await getIdToken(false);
  const response = await send(token);

  // Guard 2: replay ONCE with a forced-fresh token — only when there is an account
  // to refresh for; with none, the 401 is the honest answer.
  if (activeAccount() && (await isAuthRejection(response))) {
    const fresh = await getIdToken(true);
    if (fresh) return send(fresh);
  }
  return response;
};
